import type { FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'

interface RevealProps {
    children: ReactNode
    className?: string
    delay?: number
}

// Fades and lifts its content in once it scrolls into view. Visitors who
// prefer reduced motion see everything immediately (see globals.css).
const Reveal: FC<RevealProps> = ({ children, className, delay = 0 }): ReactNode => {
    const ref = useRef<HTMLDivElement>(null)
    const [visible, setVisible] = useState(false)

    useEffect(() => {
        const node = ref.current

        if (!node) return

        if (!('IntersectionObserver' in window)) {
            setVisible(true)
            return
        }

        const observer = new IntersectionObserver(entries => {
            if (entries.some(entry => entry.isIntersecting)) {
                setVisible(true)
                observer.disconnect()
            }
        }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 })

        observer.observe(node)

        return () => observer.disconnect()
    }, [])

    return (
        <div
            ref={ref}
            className={cn('reveal', visible && 'is-visible', className)}
            style={delay ? { transitionDelay: `${delay}ms` } : undefined}
        >
            {children}
        </div>
    )
}

export default Reveal
